import { useCallback, useEffect, useMemo } from 'react';

import {
  ActivitiesContentType,
  LanguageCodes,
  LESSON_ACTIVITIES,
} from 'global/constants';
import { useDownloadStudentPages } from './student-download-activities.hooks';
import DownloadActivitiesLanguageOnDemand from 'approot/shared/download-pages/download-activities-language-on-demand';
import { DownloadingModal } from 'approot/shared/downloading-modal/downloading-modal';
import { StudentLessonActivities } from 'approot/student-lesson/student-lesson.constants';
import { StudentAssessmentActivities } from 'approot/student-assessment/student-assessment.constants';

type Props = {
  activities: StudentLessonActivities | StudentAssessmentActivities;
  contentName: string;
  language: LanguageCodes;
  version: string;
  contentId: number;
  contentType: ActivitiesContentType;
  classId: number;
  handleClose: () => void;
};

const StudentDownloadActivitiesPdf = ({
  activities,
  contentName,
  language,
  version,
  contentId,
  contentType,
  classId,
  handleClose,
}: Props) => {
  const isLesson = contentType === LESSON_ACTIVITIES;

  const { hasError, downloadLesson, downloadAssessment } =
    useDownloadStudentPages();

  const assetKeys = useMemo(
    () => activities?.assetKeys?.print || [],
    [activities]
  );

  const currentLanguageAssetKey = useMemo(
    () => assetKeys.find(key => key.language === language)?.key,
    [assetKeys, language]
  );

  const handleDownloadDocument = useCallback(
    (assetKey: string) => {
      if (isLesson) {
        downloadLesson({
          language,
          version,
          contentId,
          contentType,
          selectedSections: [],
          assignedOverviews: activities.sectionsOverview,
          name: contentName,
          assetKey,
          onComplete: handleClose,
        });
      } else {
        downloadAssessment({
          language,
          version,
          contentId,
          selectedSections: [],
          assignedOverviews: activities.sectionsOverview,
          name: contentName,
          assetKey,
          onComplete: handleClose,
        });
      }
    },
    [
      isLesson,
      contentType,
      contentName,
      activities,
      language,
      version,
      contentId,
      downloadLesson,
      downloadAssessment,
      handleClose,
    ]
  );

  // if the language download exists we will have a currentLanguageAssetKey
  // and can download the asset right away
  useEffect(() => {
    if (currentLanguageAssetKey) {
      handleDownloadDocument(currentLanguageAssetKey);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLanguageAssetKey]);

  const error = hasError;

  return (
    <>
      <DownloadingModal
        isLoading
        title="Preparing your Activities"
        text="Please wait while we prepare your Activities."
        hasError={!!error}
        errorMessage="Unable to download the Activities. Try again"
      />
      {!currentLanguageAssetKey && (
        <DownloadActivitiesLanguageOnDemand
          isStudentDownload
          hideDefaultMessage
          contentId={contentId}
          contentType={contentType}
          classId={classId}
          language={language}
          version={version}
          onComplete={assetKeys => {
            if (assetKeys) {
              handleDownloadDocument(assetKeys[0].key);
            } else {
              handleClose();
            }
          }}
        />
      )}
    </>
  );
};

export default StudentDownloadActivitiesPdf;
